import { TrashIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useConfigurations } from "@via/api/configuration.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { GenericError } from "@via/components/error.tsx";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox.tsx";
import { OverlayLoading } from "@via/components/loading/loading";
import Modal from "@via/components/modal.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import TableContextMenu from "@via/components/table/table-context-menu";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  fetchProcessDetails,
  useCreateParameter,
  useCreateProcessMenuGroup,
  useDeleteMenuGroup,
  useDeleteParameter,
  useDeleteProcessRevision,
  useEditMenuGroup,
  useEditParameter,
  useProcessMenuGroupsList,
  useProcessExample,
  useProcessRevisionList,
} from "@via/pages/projects/pipelines/pipelines.api";
import Footer from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/create-edit-process-form-footer";
import Model from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/model.tsx";
import Overview from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/overview.tsx";
import Parameter from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/parameter.tsx";
import ProcessRunButton from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/process-run-button.tsx";
import ProcessTagsSection from "@via/components/process-tags-section.tsx";
import RevisionCommentModal, {
  ModalType,
} from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/revision-comment-modal";
import { ExampleTab } from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/example-tab.tsx";
import ProvenanceTab, {
  isProvenanceTabAvailable,
} from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/provenance-tab.tsx";
import RevisionsTab, {
  isRevisionsTabAvailable,
} from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/revisions-tab.tsx";
import ScriptAssets from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/script-assets.tsx";
import Scripts from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/scripts.tsx";
import ManageMenuGroupModal from "@via/pages/projects/pipelines/workflow/components/manage-menu-group-modal.tsx";
import ManageParametersModal from "@via/pages/projects/pipelines/workflow/components/manage-parameters-modal.tsx";
import {
  EditProcessGroupOptions,
  OperatorTypes,
  QualifierOptionsEnum,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import {
  cleanAndParseProcess,
  formatProcessDataToNewSchema,
  removeProcessPortRow,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { useParameterList } from "@via/pages/projects/runs/runs.api.tsx";
import {
  getPermissionsEncoding,
  isEditingAllowed,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  EDIT_PROCESS_TABS,
  EditProcessSections,
  LANGUAGE_MODES,
  ParameterTypes,
} from "@via/utils/constants.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import {
  EditProcessFormRequestBody,
  ParameterInterface,
  SelectOption,
  TableContextMenuInterface,
} from "@via/utils/interfaces.tsx";
import {
  AIModelTamarindConfig,
  CreateProcessParameterRequestBody,
  MenuGroupListItem,
  MenuGroupType,
  PermissionType,
  Permissions,
} from "common";
import { useEffect, useMemo, useRef, useState } from "react";
import CortexButton from "@via/components/cortex-button/cortex-button.tsx";

export const SCRIPT = "body";

type SelectRevisionOption = SelectOption & {
  processId: string;
};

interface CreateEditProcessFormProps {
  title: string;
  data: any;
  vertexModelConfig?: any | null;
  isCreateProcessRevisionLoading: boolean;
  isOverwriteRevisionLoading: boolean;
  isCreateNewProcessLoading: boolean;
  isDeleteRevisionLoading?: boolean;
  onSaveProcess: (processDraft: any) => void;
  onChangeRevision: (revision: any) => void;
  onOverwriteProcess: (
    processDraft: any,
    saveMode?: any,
    autoDeployConfig?: any
  ) => void;
  onDuplicateProcess: (processId: number) => void;
  onDeleteRevision?: (processId: number) => Promise<void>;
  onCancel: () => void;
  /** Optional callback when revision is fetched - used to fetch additional data like AI model details */
  onRevisionFetch?: (processId: number) => Promise<any>;
  /**
   * Called when the revision the form was given is deleted from inside it, with the surviving
   * revision the form has loaded instead, so the caller can point whatever used the deleted one at
   * it. Falls back to onChangeRevision.
   */
  onRevisionDeleted?: (revision: any) => void;
}

export default function CreateEditProcessForm({
  title,
  data,
  isCreateProcessRevisionLoading,
  isOverwriteRevisionLoading,
  isCreateNewProcessLoading,
  isDeleteRevisionLoading,
  onSaveProcess,
  onChangeRevision,
  onOverwriteProcess,
  onDuplicateProcess,
  onDeleteRevision,
  onCancel,
  onRevisionFetch,
  onRevisionDeleted,
}: CreateEditProcessFormProps) {
  const { setNotification } = useNotifications();
  const { user } = useAuth();
  const [isFetchProcessDetailsLoading, setIsFetchProcessDetailsLoading] =
    useState(false);
  const [draft, setDraft] = useState<any>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rawData, setRawData] = useState();
  const [selectedTab, setSelectedTab] = useState<string>(
    EditProcessSections.Overview
  );
  const [selectedFile, setSelectedFile] = useState<string>(SCRIPT);
  const [language, setLanguage] = useState(LANGUAGE_MODES.GROOVY);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [canEditProcess, setCanEditProcess] = useState(false);
  const [revisions, setRevisions] = useState<SelectRevisionOption[]>([]);
  const [selectedMenuGroup, setSelectedMenuGroup] =
    useState<SelectOption | null>(null);

  const [isRevisionChange, setIsRevisionChange] = useState(false);
  const [deploymentConfig, setDeploymentConfig] = useState<any | null>(null);
  const [isRevisionCommentModalOpen, setIsRevisionCommentModalOpen] =
    useState(false);
  const [isDeleteRevisionModalOpen, setIsDeleteRevisionModalOpen] =
    useState(false);
  const [selectedModelInputs, setSelectedModelInputs] = useState<string[]>([]);
  const { data: configurations } = useConfigurations();
  const defaultPermission = configurations?.ENABLE_SHARE_WITH_EVERYONE_PROJECT
    ? PermissionType.Public
    : PermissionType.UserOwned;
  const [permissions, setPermissions] =
    useState<PermissionType>(defaultPermission);
  const [menuGroups, setMenuGroups] = useState<SelectOption[]>([]);
  const [params, setParams] = useState<SelectOption[]>([]);
  const { data: parameters, isSuccess: isParameterListSuccess } =
    useParameterList();
  const {
    data: menuGroupsListData,
    isLoading: isMenuGroupsListLoading,
    isSuccess: isMenuGroupsListSuccess,
  } = useProcessMenuGroupsList(data?.type);
  const {
    data: processRevisionListData,
    isLoading: isProcessRevisionListLoading,
    isSuccess: isProcessRevisionListSuccess,
  } = useProcessRevisionList(data?.id);
  // Gates the Example tab. Shares its query key with ExampleTab, so react-query serves both from
  // one request. A revision with no example resolves to null rather than erroring.
  const { data: processExample } = useProcessExample(data?.id);
  const {
    mutate: createNewMenuGroup,
    data: newMenuGroupData,
    isSuccess: isCreateNewMenuGroupSuccess,
  } = useCreateProcessMenuGroup();
  const {
    mutate: editMenuGroup,
    data: editedMenuGroup,
    isSuccess: isEditMenuGroupSuccess,
  } = useEditMenuGroup();
  const {
    mutate: deleteNewMenuGroup,
    data: deletedMenuGroup,
    isSuccess: isDeleteMenuGroupSuccess,
  } = useDeleteMenuGroup();
  const { mutate: createNewParameter, isSuccess: isCreateNewParameterSuccess } =
    useCreateParameter();
  const { mutate: editParameter, isSuccess: isEditParameterSuccess } =
    useEditParameter();
  const { mutate: deleteParameter, isSuccess: isDeleteParameterSuccess } =
    useDeleteParameter();
  const {
    mutateAsync: deleteProcessRevision,
    isPending: isDeleteProcessRevisionLoading,
    isSuccess: isDeleteProcessRevisionSuccess,
    variables: deletedRevisionProcessId,
  } = useDeleteProcessRevision();

  useEffect(() => {
    if (!data) return;

    // Determine if editing is allowed and update state
    setCanEditProcess(isEditingAllowed(data));

    // Handle permissions on init
    setPermissions(getPermissionsEncoding(data?.perms));

    // Save a copy of the original data in memory
    setRawData(data);

    // Format "old schema" to new data structure if processId exists
    if (data?.id) {
      // A process that arrived without its ports is a failed load, not a portless process.
      // Drafting from it would put the user in an editor that has silently dropped their
      // parameters, and the save from that editor is the one that deleted them (#387).
      let formatted;
      try {
        formatted = formatProcessDataToNewSchema(data);
      } catch (error) {
        console.error("Failed to load process for editing:", error);
        setLoadError(
          (error as Error)?.message ?? "This process could not be loaded."
        );
        return;
      }
      setLoadError(null);
      setLanguage(formatted.script.language);
      setDraft(formatted);
    } else {
      setLoadError(null);
      setLanguage(data.script?.language);
      setDraft(data);
    }
  }, [data, isMenuGroupsListSuccess]);

  useEffect(() => {
    // Make sure our parameters list is always updated for all SelectComponents
    if (parameters) {
      const options =
        parameters?.map(param => ({
          label: param?.name,
          value: param?.id,
          identifier: param?.name,
          qualifier: param?.qualifier,
          fileType: param?.fileType,
        })) ?? [];
      setParams(options);
    }
  }, [parameters, isParameterListSuccess]);

  useEffect(() => {
    if (processRevisionListData) {
      const revs = processRevisionListData?.revisions?.map(item => {
        return {
          label: `Version: ${item?.rev_id} on \n ${formatDateString(item?.date_modified, "LL")} ${item?.rev_comment}`,
          value: item?.rev_id,
          processId: item?.id,
        };
      });
      setRevisions(revs);
    }
  }, [isProcessRevisionListSuccess, processRevisionListData]);

  useEffect(() => {
    if (isMenuGroupsListSuccess) {
      const groups = menuGroupsListData.data.map((mg: MenuGroupListItem) => {
        return { label: mg.name, value: mg.id };
      });
      const selected = groups.find(group => group.value === draft.menuGroupId);
      setSelectedMenuGroup(selected);
      setMenuGroups(groups);
    }
  }, [draft, menuGroupsListData, isMenuGroupsListSuccess]);

  // The deletion the effect below has already acted on. The delete mutation stays successful until
  // the next one, so a later refetch of the revision list would otherwise act on it again.
  const handledRevisionDeletion = useRef<unknown>(null);

  useEffect(() => {
    if (!isDeleteProcessRevisionSuccess || !revisions) return;
    if (handledRevisionDeletion.current === deletedRevisionProcessId) return;
    // The list refetches after a delete, so for a render it can still hold the deleted revision.
    // Nothing is decided from that stale list.
    if (
      revisions.some(
        rev =>
          rev?.processId?.toString() === deletedRevisionProcessId?.toString()
      )
    ) {
      return;
    }
    handledRevisionDeletion.current = deletedRevisionProcessId;

    if (revisions.length === 0) {
      // The last revision is gone, so there is nothing left to edit.
      onCancel();
      return;
    }

    // The draft is the deleted revision's content and must never be saved anywhere. Its port ids
    // belong to the deleted revision, and the overwrite this used to send with them deleted every
    // port of the revision it landed on. Replace it with a stored revision instead: the one the
    // pipeline uses when that still exists, otherwise a surviving one the pipeline is moved to.
    const pipelineRevision = revisions.find(
      rev => rev?.processId?.toString() === data?.id?.toString()
    );
    const target = pipelineRevision ?? revisions[revisions.length - 1];
    const showStoredRevision = async () => {
      setIsRevisionChange(false);
      setIsFetchProcessDetailsLoading(true);
      const loaded = await loadRevisionIntoDraft(
        target.processId,
        `Version ${target.value} could not be loaded after the deletion, so the editor was closed.`
      );
      setIsFetchProcessDetailsLoading(false);
      if (!loaded) {
        // Closing is the safe fallback: the only draft left is the deleted revision's.
        onCancel();
        return;
      }
      if (!pipelineRevision) {
        (onRevisionDeleted ?? onChangeRevision)(loaded.data);
      }
    };
    void showStoredRevision();
  }, [isDeleteProcessRevisionSuccess, deletedRevisionProcessId, revisions]);

  const generateNewParameter = parameter => {
    return {
      parameterId: parameter.id,
      originalName: parameter.name,
      operator: "",
      operatorContent: "",
      // regEx, not regExp. The old spelling here matched nothing on the request body or the
      // backend, so it was never sent and never stored.
      regEx: "",
      optional: false,
      test: "",
      fileType: parameter.fileType,
      qualifier: parameter.qualifier,
    };
  };

  /** Updating Fields on Overview Tab **/
  const handleOverviewChange = (type, value) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleMenuGroupUpdate = selected => {
    const { label, value } = selected;
    setDraft(prevData => ({
      ...prevData,
      menuGroupName: label,
      menuGroupId: value,
    }));
  };

  /** Removing Input Parameters if data is null */
  const wrapInputParameterChange = (fn, info) => data => {
    if (data) {
      fn(data);
    } else {
      const willDeletedItemIndex = info.row.index;
      const modified = draft.inputParameters.map((item, index) => {
        if (index === willDeletedItemIndex) {
          return {
            ...item,
            parameterId: null,
            originalName: "",
            fileType: "",
            qualifier: "",
          };
        }
        return item;
      });
      setDraft(prevData => ({
        ...prevData,
        inputParameters: modified,
      }));
    }
  };

  /** Updating Input Parameters **/
  const handleInputParameterChange = data => {
    const { value } = data;
    setDraft(prevData => ({
      ...prevData,
      inputParameters: prevData.inputParameters.map((item, index) =>
        index === value.original
          ? {
              ...item,
              parameterId: value.id,
              fileType: value.fileType,
              originalName: value.name,
              qualifier: value.qualifier,
            }
          : item
      ),
    }));
  };

  const handleInputOperatorChange = (selected, rowIndex) => {
    setDraft(prevData => ({
      ...prevData,
      inputParameters: prevData.inputParameters.map((item, index) =>
        index === rowIndex
          ? {
              ...item,
              operator: selected?.label || "",
            }
          : item
      ),
    }));
  };

  const handleNewInputParameter = selected => {
    if (!selected) return;
    const parameter = parameters.find(
      parameter => parameter.id === selected?.value
    );
    const newInput = generateNewParameter(parameter);
    setDraft(prevData => ({
      ...prevData,
      inputParameters: [...prevData.inputParameters, newInput],
    }));
  };

  /** Removing Output Parameters if data is null */
  const wrapOutputParameterChange = (fn, info) => data => {
    if (data) {
      fn(data);
    } else {
      const willDeletedItemIndex = info.row.index;
      const modified = draft.outputParameters.map((item, index) => {
        if (index === willDeletedItemIndex) {
          return {
            ...item,
            parameterId: null,
            originalName: "",
            fileType: "",
            qualifier: "",
          };
        }
        return item;
      });
      setDraft(prevData => ({
        ...prevData,
        outputParameters: modified,
      }));
    }
  };

  /** Updating Output Parameters **/
  const handleOutputParameterChange = data => {
    const { value } = data;
    setDraft(prevData => ({
      ...prevData,
      outputParameters: prevData.outputParameters.map((item, index) =>
        index === value.original
          ? {
              ...item,
              parameterId: value.id,
              fileType: value.fileType,
              originalName: value.name,
              qualifier: value.qualifier,
            }
          : item
      ),
    }));
  };

  const handleOutputOperatorChange = (selected, rowIndex) => {
    setDraft(prevData => ({
      ...prevData,
      outputParameters: prevData.outputParameters.map((item, index) =>
        index === rowIndex
          ? {
              ...item,
              operator: selected?.label || "",
            }
          : item
      ),
    }));
  };

  const handleNewOutputParameter = selected => {
    if (!selected) return;
    const parameter = parameters.find(
      parameter => parameter.id === selected.value
    );
    const newOutput = generateNewParameter(parameter);
    setDraft(prevData => ({
      ...prevData,
      outputParameters: [...prevData.outputParameters, newOutput],
    }));
  };

  // Load initial selected inputs from config
  useEffect(() => {
    if (data?.modelData?.config) {
      const config = data.modelData.config as AIModelTamarindConfig;
      if (config.userInputSettings && Array.isArray(config.userInputSettings)) {
        // Extract selected input names from userInputSettings
        const selectedNames = config.userInputSettings.map(
          setting => setting.name
        );
        setSelectedModelInputs(selectedNames);
      }
    }
  }, [data?.modelData?.config]);

  // Handle selection change
  const handleSelectionChange = (selectedInputs: string[]) => {
    setSelectedModelInputs(selectedInputs);
  };

  /** Generic Updates (Both Input / Output Parameter Settings **/
  const handleOperatorContentChange = (type, data, value) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: prevData[type].map((item, index) =>
        index === data.index ? { ...item, operatorContent: value } : item
      ),
    }));
  };

  /**
   * Outputs only. This is the publish pattern the run generator uses instead of the one it
   * would derive from the parameter name, so clearing it silently moves where a run's outputs
   * land. It is shown here so that is a visible choice rather than a side effect of saving.
   */
  const handleRegExChange = (type, data, value) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: prevData[type].map((item, index) =>
        index === data.index ? { ...item, regEx: value } : item
      ),
    }));
  };

  const handleParameterNameChange = (type, data, value) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: prevData[type].map((item, index) =>
        index === data.index ? { ...item, displayName: value } : item
      ),
    }));
  };

  const handleParameterCheckboxToggle = (type, data) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: prevData[type].map((item, index) =>
        index === data.index ? { ...item, optional: !item.optional } : item
      ),
    }));
  };

  const handleParameterDelete = (type, data) => {
    if (!canEditProcess) {
      return;
    }
    setDraft(prevData => removeProcessPortRow(prevData, type, data.index));
  };

  const handleParameterTestValueChange = (type, data, value) => {
    setDraft(prevData => ({
      ...prevData,
      [type]: prevData[type].map((item, index) =>
        index === data.index ? { ...item, test: value } : item
      ),
    }));
  };

  /** Updating Permissions Handlers **/
  const handlePermissionUpdate = (type, value) => {
    setDraft(prevData => ({
      ...prevData,
      permissionSettings: {
        ...prevData.permissionSettings,
        viewPermissions: value,
        sharedGroupId:
          value === Permissions.UserOwned
            ? null
            : prevData.permissionSettings.groupId,
      },
    }));

    setPermissions(type);
  };

  const handleGroupIdUpdate = selected => {
    setDraft(prevData => ({
      ...prevData,
      permissionSettings: {
        ...prevData.permissionSettings,
        viewPermissions: Permissions.GroupShared,
        sharedGroupId:
          prevData.permissionSettings.viewPermissions ===
          Permissions.GroupShared
            ? selected.value
            : null,
      },
    }));
    if (selected.value && permissions !== PermissionType.GroupShared) {
      setPermissions(PermissionType.GroupShared);
    }
  };

  const handleWriteGroupIdUpdate = selected => {
    const writeGroupIds = selected.map(option => option.value);
    setDraft(prevData => ({
      ...prevData,
      permissionSettings: {
        ...prevData.permissionSettings,
        writeGroupIds,
      },
    }));
  };

  const columnHelper = createColumnHelper<EditProcessFormRequestBody>();
  const inputColumns = [
    columnHelper.accessor("id", {
      cell: info => {
        return (
          <button
            id={`input-parameter-remove-row-button-${info.row.index}`}
            className="cursor-pointer mr-1 h-9 max-w-9 rounded-md border-[1px] px-1.5"
            onClick={() =>
              handleParameterDelete(ParameterTypes.Inputs, info.row)
            }>
            <TrashIcon className="h-5 w-5" />
          </button>
        );
      },
      header: "",
      size: 50,
    }),
    columnHelper.accessor("inputParameters", {
      cell: info => {
        return (
          <div
            className="w-80 pr-3"
            id={`input-parameter-dropdown-${info.row.index}`}>
            <SelectComponent
              onChange={data =>
                wrapInputParameterChange(handleInputParameterChange, info)(data)
              }
              readOnly={!canEditProcess}
              name="inputDetails"
              showFullSelectedText
              defaultValue={{
                label: info.row.original.parameterId
                  ? info.row.original.qualifier === QualifierOptionsEnum.Val
                    ? `${info.row.original.originalName} \n (${info.row.original.qualifier})`
                    : `${info.row.original.originalName} \n (${info.row.original.fileType}, ${info.row.original.qualifier})`
                  : "",
                value: info.row.index,
              }}
              enableClear={true}
              options={
                parameters.map(option => ({
                  label:
                    option.qualifier === QualifierOptionsEnum.Val
                      ? `${option.name} \n (${option.qualifier})`
                      : `${option.name} \n (${option.fileType}, ${option.qualifier})`,
                  value: {
                    ...option,
                    original: info.row.index,
                  },
                })) ?? []
              }
              rules={{ required: true }}
            />
          </div>
        );
      },
      header: "Input Parameters",
    }),
    columnHelper.accessor("displayName", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`input-name-${info.row.index}`}
              defaultValue={info.row.original?.displayName}
              disabled={!canEditProcess}
              onBlur={e =>
                handleParameterNameChange(
                  ParameterTypes.Inputs,
                  info.row,
                  e.target.value
                )
              }
              name={info.row.original.displayName}
              type="text"
              className="mr-1 block w-60 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Input Name",
    }),
    columnHelper.accessor("operator", {
      cell: info => {
        let defaultValue;
        if (info.row.original.operator.length) {
          defaultValue = {
            label: info.row.original.operator,
            value: info.row.original,
          };
        }
        return (
          <div
            className="w-48 pr-3"
            id={`input-operator-dropdown-${info.row.index}`}>
            <SelectComponent
              onChange={e => handleInputOperatorChange(e, info.row.index)}
              readOnly={!canEditProcess}
              name="operators"
              defaultValue={defaultValue}
              enableClear={true}
              options={
                OperatorTypes.map(option => ({
                  label: option,
                  value: info.row.index,
                })) ?? []
              }
            />
          </div>
        );
      },
      header: "Operator",
    }),
    columnHelper.accessor("operatorContent", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`input-operator-content-${info.row.index}`}
              defaultValue={info.row.original?.operatorContent}
              disabled={!canEditProcess}
              placeholder="Enter Operator Content"
              onBlur={e =>
                handleOperatorContentChange(
                  ParameterTypes.Inputs,
                  info.row,
                  e.target.value
                )
              }
              name={info.row.original.operatorContent}
              type="text"
              className="mr-1 block w-48 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Operator Content",
    }),
    columnHelper.accessor("operator", {
      cell: info => {
        return (
          <div
            className="flex justify-center"
            id={`input-optional-checkbox-${info.row.index}`}>
            <IndeterminateCheckbox
              checked={info.row.original.optional}
              disabled={!canEditProcess}
              onChange={() => {
                info.row.toggleSelected();
                handleParameterCheckboxToggle(ParameterTypes.Inputs, info.row);
              }}
              onClick={e => {
                e.stopPropagation();
              }}
            />
          </div>
        );
      },
      header: "Optional",
      size: 40,
    }),
    columnHelper.accessor("test", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`input-test-value-${info.row.index}`}
              defaultValue={info.row.original?.test}
              disabled={!canEditProcess}
              placeholder="Enter Test Value"
              onBlur={e =>
                handleParameterTestValueChange(
                  ParameterTypes.Inputs,
                  info.row,
                  e.target.value
                )
              }
              name={info.row.original.test}
              type="text"
              className="mr-1 block w-52 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Test Value",
    }),
  ] as Array<ColumnDef<CreateProcessParameterRequestBody>>;

  const outputColumns = [
    columnHelper.accessor("id", {
      cell: info => {
        return (
          <button
            id={`output-parameter-remove-row-button-${info.row.index}`}
            className="cursor-pointer mr-1 h-9 max-w-9 rounded-md border-[1px] px-1.5"
            onClick={() =>
              handleParameterDelete(ParameterTypes.Outputs, info.row)
            }>
            <TrashIcon className="h-5 w-5" />
          </button>
        );
      },
      header: "",
      size: 50,
    }),
    columnHelper.accessor("outputParameters", {
      cell: info => {
        return (
          <div
            className="w-80 pr-3"
            id={`output-parameter-dropdown-${info.row.index}`}>
            <SelectComponent
              onChange={data =>
                wrapOutputParameterChange(
                  handleOutputParameterChange,
                  info
                )(data)
              }
              readOnly={!canEditProcess}
              name="outputDetails"
              showFullSelectedText
              defaultValue={{
                label: info.row.original.parameterId
                  ? info.row.original.qualifier === QualifierOptionsEnum.Val
                    ? `${info.row.original.originalName} \n (${info.row.original.qualifier})`
                    : `${info.row.original.originalName} \n (${info.row.original.fileType}, ${info.row.original.qualifier})`
                  : "",
                value: info.row.index,
              }}
              enableClear={true}
              options={
                parameters.map(option => ({
                  label:
                    option.qualifier === QualifierOptionsEnum.Val
                      ? `${option.name} \n (${option.qualifier})`
                      : `${option.name} \n (${option.fileType}, ${option.qualifier})`,
                  value: {
                    ...option,
                    original: info.row.index,
                  },
                })) ?? []
              }
              rules={{ required: true }}
            />
          </div>
        );
      },
      header: "Output Parameters",
    }),
    columnHelper.accessor("displayName", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`output-name-${info.row.index}`}
              defaultValue={info.row.original?.displayName}
              disabled={!canEditProcess}
              onBlur={e =>
                handleParameterNameChange(
                  ParameterTypes.Outputs,
                  info.row,
                  e.target.value
                )
              }
              name={info.row.original.displayName}
              type="text"
              className="mr-1 block w-60 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Output Name",
    }),
    columnHelper.accessor("operator", {
      cell: info => {
        let defaultValue;
        if (info.row.original.operator.length) {
          defaultValue = {
            label: info.row.original.operator,
            value: info.row.original,
          };
        }
        return (
          <div
            className="w-48 pr-3"
            id={`output-operator-dropdown-${info.row.index}`}>
            <SelectComponent
              onChange={e => handleOutputOperatorChange(e, info.row.index)}
              readOnly={!canEditProcess}
              name="operators"
              defaultValue={defaultValue}
              enableClear={true}
              options={
                OperatorTypes.map(option => ({
                  label: option,
                  value: info.row.index,
                })) ?? []
              }
            />
          </div>
        );
      },
      header: "Operator",
    }),
    columnHelper.accessor("operatorContent", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`output-operator-content-${info.row.index}`}
              defaultValue={info.row.original?.operatorContent}
              disabled={!canEditProcess}
              placeholder="Enter Operator Content"
              onBlur={e =>
                handleOperatorContentChange(
                  ParameterTypes.Outputs,
                  info.row,
                  e.target.value
                )
              }
              name={info.row.original.operatorContent}
              type="text"
              className="mr-1 block w-48 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Operator Content",
    }),
    columnHelper.accessor("regEx", {
      cell: info => {
        return (
          <div className="flex flex-row items-center">
            <input
              id={`output-reg-ex-${info.row.index}`}
              defaultValue={info.row.original?.regEx}
              disabled={!canEditProcess}
              placeholder="Derived from name"
              title="Overrides the publish pattern derived from the parameter name. Leave blank to use the derived one."
              onBlur={e =>
                handleRegExChange(
                  ParameterTypes.Outputs,
                  info.row,
                  e.target.value
                )
              }
              type="text"
              className="mr-1 block w-48 rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        );
      },
      header: "Publish Pattern",
    }),
    columnHelper.accessor("operator", {
      cell: info => {
        return (
          <div
            className="flex justify-center"
            id={`output-optional-checkbox-${info.row.index}`}>
            <IndeterminateCheckbox
              checked={info.row.original.optional}
              disabled={!canEditProcess}
              onChange={() => {
                info.row.toggleSelected();
                handleParameterCheckboxToggle(ParameterTypes.Outputs, info.row);
              }}
              onClick={e => {
                e.stopPropagation();
              }}
            />
          </div>
        );
      },
      header: "Optional",
      size: 40,
    }),
  ] as Array<ColumnDef<CreateProcessParameterRequestBody>>;

  /** Callback for Updating Script Code Based onKeyDown **/
  const handleEditorChange = (value: string | undefined): void => {
    const modified = {
      ...draft,
      script: { ...draft?.script, [selectedFile]: value },
    };
    setDraft(modified);
  };

  /** Set Language Based on SelectComponent for Syntax Highlighting **/
  const handleLanguageChange = selected => {
    const modified = {
      ...draft,
      script: { ...draft?.script, language: selected.value },
    };
    setLanguage(selected.value);
    setDraft(modified);
  };

  const onCreateMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.CreateMenuGroup);
    setOpenModal(true);
  };

  const onEditMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.EditMenuGroup);
    setOpenModal(true);
  };

  const onDeleteMenuGroup = () => {
    setModalContent(EditProcessGroupOptions.DeleteMenuGroup);
    setOpenModal(true);
  };

  const onCreateParameter = () => {
    setModalContent(EditProcessGroupOptions.CreateParameter);
    setOpenModal(true);
  };

  const onEditParameter = () => {
    setModalContent(EditProcessGroupOptions.EditParameter);
    setOpenModal(true);
  };

  const onDeleteParameter = () => {
    setModalContent(EditProcessGroupOptions.DeleteParameter);
    setOpenModal(true);
  };

  useEffect(() => {
    if (isCreateNewMenuGroupSuccess) {
      setDraft(prevData => ({
        ...prevData,
        menuGroupName: newMenuGroupData?.name,
        menuGroupId: newMenuGroupData?.id,
      }));
      const selected = {
        label: newMenuGroupData?.name,
        value: newMenuGroupData?.id,
      };
      setSelectedMenuGroup(selected);
      setOpenModal(false);
    }
  }, [isCreateNewMenuGroupSuccess]);

  useEffect(() => {
    if (isEditMenuGroupSuccess) {
      setDraft(prevData => ({
        ...prevData,
        menuGroupName: editedMenuGroup?.name,
        menuGroupId: editedMenuGroup?.id,
      }));
      const selected = {
        label: editedMenuGroup?.name,
        value: editedMenuGroup?.id,
      };
      setSelectedMenuGroup(selected);
      setOpenModal(false);
    }
  }, [isEditMenuGroupSuccess, editedMenuGroup]);

  useEffect(() => {
    if (isDeleteMenuGroupSuccess) {
      const menuGroup = menuGroups.find(
        group => group.value === draft?.menuGroupId
      );
      if (!menuGroup) {
        const selected = menuGroups[0];
        setSelectedMenuGroup(selected);
        setDraft(prevData => ({
          ...prevData,
          menuGroupName: selected.label,
          menuGroupId: selected.value,
        }));
      }
    }
  }, [isDeleteMenuGroupSuccess, deletedMenuGroup, menuGroups]);

  useEffect(() => {
    if (
      isCreateNewParameterSuccess ||
      isEditParameterSuccess ||
      isDeleteParameterSuccess
    ) {
      setOpenModal(false);
    }
  }, [
    isCreateNewParameterSuccess,
    isEditParameterSuccess,
    isDeleteParameterSuccess,
  ]);

  const handleCreateMenuGroup = value => {
    const payload = {
      name: value,
      type: data?.type,
    };
    createNewMenuGroup(payload);
    setOpenModal(false);
  };

  const handleEditMenuGroup = formPayload => {
    const { menuGroup, name } = formPayload;
    const payload = { value: menuGroup, rename: name, type: data?.type };
    editMenuGroup(payload);
    setOpenModal(false);
  };

  const handleDeleteMenuGroup = selected => {
    deleteNewMenuGroup(selected);
    setOpenModal(false);
  };

  const handleCreateParameter = (payload: ParameterInterface): void => {
    createNewParameter(payload);
  };

  const handleEditParameter = (payload: ParameterInterface): void => {
    editParameter(payload);
  };

  const handleDeleteParameter = (id: number): void => {
    deleteParameter(id);
  };

  const handleProcessRevisionChange = async selected => {
    setIsRevisionChange(selected.value !== draft?.revisionId);
    const { revisions } = processRevisionListData;
    const retrieved = revisions.find(d => d.rev_id === selected.value);
    setIsFetchProcessDetailsLoading(true);

    const loaded = await loadRevisionIntoDraft(
      retrieved.id,
      `Version ${selected.value} could not be loaded. You are still editing the revision you had open.`
    );
    if (!loaded) {
      // A fetch that rejects here used to leave the drawer spinning forever.
      setIsRevisionChange(false);
      setIsFetchProcessDetailsLoading(false);
      return;
    }

    setNotification({
      id: "process-revision-change",
      message: "New Revision Successfully Selected",
      details: `You are now editing version ${loaded.formatted.revisionId} of ${loaded.formatted.name}`,
      variant: NOTIFICATION.SUCCESS,
    });
    setIsFetchProcessDetailsLoading(false);
  };

  /**
   * Replaces the draft with a stored revision. Returns the fetched process and the draft built
   * from it, or null when it could not be loaded, in which case the draft is left alone and the
   * error is shown with `failureDetails` unless the error carries its own message.
   */
  const loadRevisionIntoDraft = async (processId, failureDetails: string) => {
    // A revision that cannot be loaded leaves the draft alone. Swapping in a draft built from a
    // response that lost its ports is how a failed load reached a save (#387).
    let data;
    let formatted;
    try {
      data = await fetchProcessDetails(processId);
      const parsed = cleanAndParseProcess(data);
      formatted = formatProcessDataToNewSchema(parsed);
    } catch (error) {
      console.error("Failed to load process revision:", error);
      setNotification({
        id: "process-revision-change-failed",
        message: "Revision Could Not Be Loaded",
        details: (error as Error)?.message ?? failureDetails,
        variant: NOTIFICATION.ERROR,
      });
      return null;
    }

    setRawData(data);
    setPermissions(getPermissionsEncoding(data?.perms));

    // If onRevisionFetch callback is provided, fetch additional data (e.g., AI model details)
    if (onRevisionFetch) {
      try {
        const additionalData = await onRevisionFetch(processId);
        if (additionalData) {
          (formatted as any).modelData = additionalData;
        }
      } catch (error) {
        console.error("Failed to fetch additional revision data:", error);
      }
    }

    setLanguage(formatted?.script?.language);
    setDraft(formatted);
    return { data, formatted };
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  const modals = useMemo(
    () => ({
      [EditProcessGroupOptions.CreateMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.CreateMenuGroup}
          menuGroups={menuGroups}
          onCancel={handleCancel}
          onSubmit={handleCreateMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
      [EditProcessGroupOptions.EditMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.EditMenuGroup}
          menuGroups={menuGroups}
          onCancel={handleCancel}
          onSubmit={handleEditMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
      [EditProcessGroupOptions.DeleteMenuGroup]: (
        <ManageMenuGroupModal
          action={EditProcessGroupOptions.DeleteMenuGroup}
          menuGroups={menuGroups}
          onCancel={handleCancel}
          onSubmit={handleDeleteMenuGroup}
          menuGroup={selectedMenuGroup}
        />
      ),
      [EditProcessGroupOptions.CreateParameter]: (
        <ManageParametersModal
          action={EditProcessGroupOptions.CreateParameter}
          parameters={params}
          onCancel={handleCancel}
          onSubmit={handleCreateParameter}
        />
      ),
      [EditProcessGroupOptions.EditParameter]: (
        <ManageParametersModal
          action={EditProcessGroupOptions.EditParameter}
          parameters={params}
          onCancel={handleCancel}
          onSubmit={handleEditParameter}
        />
      ),
      [EditProcessGroupOptions.DeleteParameter]: (
        <ManageParametersModal
          action={EditProcessGroupOptions.DeleteParameter}
          parameters={params}
          onCancel={handleCancel}
          onSubmit={handleDeleteParameter}
        />
      ),
    }),
    [modalContent, menuGroups, menuGroupsListData, parameters, params]
  );

  const availableTabs = useMemo(() => {
    return EDIT_PROCESS_TABS.filter(tab => {
      // Show Model tab only if data has modelData prop
      if (tab.name === EditProcessSections.Model) {
        return data?.modelData != null;
      }
      // Revisions needs two revisions to compare. Deliberately NOT gated on a git connection:
      // both sides render from the database, so comparison works with no repository and no
      // network, and gating on git would hide a working feature behind a setting configured on
      // another screen. Hidden at one revision because there is nothing to compare, and a tab
      // that renders empty teaches people the feature is broken.
      if (tab.name === EditProcessSections.Revisions) {
        return isRevisionsTabAvailable(revisions?.length ?? 0);
      }
      // An example exists only after a passing test has been captured, so most revisions have
      // none. Hidden until there is one, for the same reason Revisions is hidden at one revision:
      // a tab that renders empty teaches people the feature is broken.
      if (tab.name === EditProcessSections.Example) {
        return processExample != null;
      }
      // The provenance read needs write permission (git-4-2), so the tab is offered only to
      // someone the server will answer, and only once the process exists.
      if (tab.name === EditProcessSections.Provenance) {
        return isProvenanceTabAvailable(data?.id, canEditProcess);
      }
      // Show all other tabs by default
      return true;
    });
  }, [
    data?.modelData,
    data?.id,
    canEditProcess,
    revisions?.length,
    processExample,
  ]);

  // The standalone run route synthesizes a Nextflow one node pipeline for a plain process; it
  // has no notion of running an AI model or a model skill. data?.type arrives as either the
  // numeric MenuGroupType or its string form depending on the caller (compare footer.tsx's
  // hideSelectRevision check against tableContextMenuOptions above, which check the two
  // differently), so both forms are checked here rather than assuming one.
  const isPlainProcess = useMemo(() => {
    return (
      !data?.modelData &&
      data?.type !== MenuGroupType.AI_MODEL &&
      data?.type !== MenuGroupType.AI_MODEL.toString() &&
      data?.type !== MenuGroupType.MODEL_SKILL &&
      data?.type !== MenuGroupType.MODEL_SKILL.toString()
    );
  }, [data?.modelData, data?.type]);

  // Tag rights belong to the process family and come from the catalog, which
  // ProcessTagsSection fetches only when it is shown; a user who may edit this revision can
  // still be refused tag edits.
  const showTags =
    !draft?.isNewProcess && data?.process_gid != null && isPlainProcess;

  const tableContextMenuOptions = useMemo(() => {
    const options: TableContextMenuInterface[] = [];

    // Determine the duplicate option name based on process type
    const getDuplicateName = () => {
      if (data?.type === MenuGroupType.AI_MODEL) {
        return "Duplicate AI Model";
      }
      if (data?.type === MenuGroupType.MODEL_SKILL) {
        return "Duplicate Model Skill";
      }
      return "Duplicate Process";
    };

    if (canEditProcess) {
      options.push({
        name: "Create Revision",
        href: "#",
        id: "create-revision",
      });
      options.push({
        name: "Delete Revision",
        href: "#",
        id: "delete-revision",
      });
      if (!draft?.isNewProcess) {
        options.push({
          name: getDuplicateName(),
          href: "#",
          id: "duplicate-process",
        });
      }
    } else {
      if (
        draft?.permissionSettings?.viewPermissions !== Permissions.UserOwned
      ) {
        options.push({
          name: getDuplicateName(),
          href: "#",
          id: "duplicate-process",
        });
      }
    }
    return options;
  }, [draft?.isNewProcess, canEditProcess, data?.type]);

  const handleContextMenuSelection = async (
    action: TableContextMenuInterface
  ) => {
    switch (action.name) {
      case "Create Revision":
        setIsRevisionCommentModalOpen(true);
        break;
      case "Delete Revision":
        setIsDeleteRevisionModalOpen(true);
        break;
      case "Duplicate Process":
      case "Duplicate AI Model":
      case "Duplicate Model Skill":
        onDuplicateProcess(draft?.processId);
        break;
      default:
        break;
    }
  };

  if (loadError) {
    return (
      <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold leading-6 text-gray-900">
              {title}
            </h2>
            <button
              type="button"
              className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
              onClick={() => onCancel()}>
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="via-generic-error flex-1">
          <GenericError message={loadError} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold leading-6 text-gray-900">
              {title}
            </h2>
            <div className="ml-3 flex h-7 items-center gap-2">
              {/* Only a saved, plain process can be run standalone: a brand new unsaved
                  draft has no processId yet, an AI model / model skill process is not what
                  the standalone run route synthesizes a Nextflow pipeline for, and running
                  is an authoring action gated on write permission the same way Save is. */}
              {!draft?.isNewProcess && isPlainProcess && canEditProcess && (
                <ProcessRunButton processId={data?.id} />
              )}
              {user?.featureFlags?.FF_AI_REPORT_INTERPRET && (
                <div className="h-7">
                  <CortexButton />
                </div>
              )}
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => onCancel()}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="mt-1">
              <p className="font-bold text-gray-900 text-2xl leading-10">
                {draft?.name}
              </p>
              {!draft?.isNewProcess && (
                <p className="pt-1 text-xs">
                  Created by {draft?.createdBy} on{" "}
                  {formatDateString(draft?.createdAt, "LL")} &#8226; Last edited
                  on {formatDateString(draft?.lastModified, "LL")}{" "}
                </p>
              )}
            </div>
            {!draft?.isNewProcess && (
              <div className="flex items-center gap-3">
                <div className="max-w-64">
                  <SelectComponent
                    name="revisions"
                    value={revisions?.find(r => r.value === draft.revisionId)}
                    enableClear={false}
                    options={revisions}
                    isLoading={
                      isProcessRevisionListLoading ||
                      isFetchProcessDetailsLoading
                    }
                    onChange={handleProcessRevisionChange}
                    placeholder={"Select a Revision"}
                  />
                </div>
                {tableContextMenuOptions.length > 0 && (
                  <TableContextMenu
                    className="rounded-md border border-indigo-600 px-2 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:border-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    options={tableContextMenuOptions}
                    onSelect={handleContextMenuSelection}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="hidden sm:block">
              <div className="border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {availableTabs.map(tab => (
                    <div
                      key={tab.name}
                      className={classNames(
                        selectedTab === tab.name
                          ? "border-indigo-500 text-indigo-600"
                          : "border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700",
                        "flex cursor-pointer whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium duration-300 ease-in-out"
                      )}
                      onClick={() => setSelectedTab(tab.name)}>
                      {tab.name}
                    </div>
                  ))}
                </nav>
              </div>
            </div>
            <div className="space-y-4 pb-5">
              {selectedTab === EditProcessSections.Overview && (
                <Overview
                  draft={draft}
                  configurations={configurations}
                  canEditProcess={canEditProcess}
                  handleOverviewChange={handleOverviewChange}
                  handleMenuGroupUpdate={handleMenuGroupUpdate}
                  handlePermissionUpdate={handlePermissionUpdate}
                  handleGroupIdUpdate={handleGroupIdUpdate}
                  onCreateMenuGroup={onCreateMenuGroup}
                  onEditMenuGroup={onEditMenuGroup}
                  onDeleteMenuGroup={onDeleteMenuGroup}
                  permissions={permissions}
                  selectedGroup={draft?.permissionSettings?.sharedGroupId}
                  selectedMenuGroup={selectedMenuGroup}
                  menuGroups={menuGroups}
                  isMenuGroupsListLoading={isMenuGroupsListLoading}
                  selectedWriteUserGroups={
                    draft?.permissionSettings?.writeGroupIds
                  }
                  handleWriteGroupIdUpdate={handleWriteGroupIdUpdate}
                  handleCreateMenuGroup={handleCreateMenuGroup}
                />
              )}
              {selectedTab === EditProcessSections.Overview && showTags && (
                <ProcessTagsSection processGid={Number(data.process_gid)} />
              )}
              <Modal
                open={openModal}
                setOpen={setOpenModal}
                showButtons={false}>
                {modalContent && modals[modalContent]}
              </Modal>
              {isRevisionCommentModalOpen && (
                <RevisionCommentModal
                  type={ModalType.CREATE_REVISION}
                  setOpen={() => setIsRevisionCommentModalOpen(false)}
                  draft={draft}
                  isLoading={isCreateProcessRevisionLoading}
                  onConfirm={revisionComment => {
                    // Validate auto deploy configuration
                    if (
                      deploymentConfig &&
                      deploymentConfig.autoDeployEnabled === true &&
                      !deploymentConfig.autoDeployConfig
                    ) {
                      setNotification({
                        type: "error",
                        message:
                          "Auto Deploy is enabled but deployment configuration is not set. Please set a deployment configuration or disable Auto Deploy.",
                      });
                      return;
                    }
                    const payload = {
                      ...draft,
                      revisionComment,
                      deploymentConfig,
                    };
                    onSaveProcess(payload);
                    setIsRevisionCommentModalOpen(false);
                  }}
                />
              )}
              {isDeleteRevisionModalOpen && (
                <DeleteModal
                  item={draft}
                  setItem={setIsDeleteRevisionModalOpen}
                  deleteFunction={() =>
                    onDeleteRevision
                      ? onDeleteRevision(draft?.processId)
                      : deleteProcessRevision(draft?.processId)
                  }
                  isLoading={
                    isDeleteRevisionLoading ?? isDeleteProcessRevisionLoading
                  }
                  title="Delete Process Revision"
                  message={() => (
                    <>
                      Are you sure you want to delete the revision{" "}
                      <span className="font-semibold">{draft?.revisionId}</span>{" "}
                      of the process{" "}
                      <span className="font-semibold">{draft?.name}</span>?
                    </>
                  )}
                />
              )}
              {selectedTab === EditProcessSections.Parameters && (
                <Parameter
                  draft={draft}
                  parameters={parameters}
                  inputColumns={inputColumns}
                  outputColumns={outputColumns}
                  canEditProcess={canEditProcess}
                  onCreateParameter={onCreateParameter}
                  onEditParameter={onEditParameter}
                  onDeleteParameter={onDeleteParameter}
                  handleNewInputParameter={handleNewInputParameter}
                  handleNewOutputParameter={handleNewOutputParameter}
                />
              )}
              {selectedTab === EditProcessSections.Scripts && (
                <Scripts
                  draft={draft}
                  language={language}
                  handleEditorChange={handleEditorChange}
                  handleLanguageChange={handleLanguageChange}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  canEditProcess={canEditProcess}
                />
              )}
              {selectedTab === EditProcessSections.Files && (
                <ScriptAssets
                  processId={data?.id}
                  canEditProcess={canEditProcess}
                />
              )}
              {selectedTab === EditProcessSections.Revisions && (
                <RevisionsTab
                  processId={data?.id}
                  canEditProcess={canEditProcess}
                />
              )}
              {selectedTab === EditProcessSections.Example && (
                <ExampleTab processId={data?.id} />
              )}
              {selectedTab === EditProcessSections.Provenance && (
                <ProvenanceTab processId={data?.id} />
              )}
              {selectedTab === EditProcessSections.Model && (
                <Model
                  modelData={data?.modelData}
                  deploymentConfig={deploymentConfig}
                  onDeploymentConfigChange={setDeploymentConfig}
                  onSelectionChange={handleSelectionChange}
                  initialSelectedInputs={selectedModelInputs}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer
        canEditProcess={canEditProcess}
        isCreateProcessRevisionLoading={isCreateProcessRevisionLoading}
        isOverwriteRevisionLoading={isOverwriteRevisionLoading}
        onSaveProcess={onSaveProcess}
        onCancel={onCancel}
        draft={draft}
        rawData={rawData}
        isRevisionChange={isRevisionChange}
        onChangeRevision={() => {
          setIsRevisionChange(false);
          onChangeRevision(rawData);
        }}
        onOverwriteProcess={(processDraft, saveMode, autoDeployConfig) => {
          // Include selectedModelInputs in the draft when calling onOverwriteProcess
          const draftWithInputs = {
            ...processDraft,
            ...(selectedModelInputs &&
              selectedModelInputs.length > 0 && {
                selectedModelInputs,
              }),
          };
          onOverwriteProcess(draftWithInputs, saveMode, autoDeployConfig);
        }}
        deploymentConfig={deploymentConfig}
        selectedModelInputs={selectedModelInputs}
        hideSelectRevision={
          data?.type === MenuGroupType.AI_MODEL.toString() ||
          data?.type === MenuGroupType.MODEL_SKILL.toString()
        }
      />
      {(isFetchProcessDetailsLoading ||
        isOverwriteRevisionLoading ||
        isCreateProcessRevisionLoading ||
        isCreateNewProcessLoading) && <OverlayLoading />}
    </div>
  );
}
